/**
 * Pure structural predicates for which pages a page link may target.
 *
 * Shared by the server-side localization catalogs (`getValidPageIds`) and the
 * WFS page-link catalog (`createPageCatalog`) so eligibility cannot drift
 * between the two. Deliberately leaf: no server/Mongo imports and no
 * PageManager/PageRecord graph — callers map their own shapes onto
 * {@link PageLinkTargetMetadata}.
 */

/**
 * Static Usys utility pages that stay in the PageList and the page-link
 * catalog. Lives here (re-exported by PageManagerUtils) so eligibility
 * checks can read it without importing the PageManager module graph.
 */
export const allowedStaticUsysPages = new Set([
  'usys-log-in',
  'usys-sign-up',
  'usys-user-account',
  'usys-reset-password',
]);

/**
 * The page metadata the static-link eligibility predicate reads. All fields
 * optional so callers pass raw shapes directly (`RawPageType` carries these
 * as nullable/optional flags); absent metadata reads as a main-tree static
 * page, matching the historical catalogs' behavior for sparse inputs.
 */
export type PageLinkTargetMetadata = {
  /**
   * CMS template-page binding (`page.dynCollection` on the wire). A dynamic
   * page is reached through a collection item, never a static link.
   */
  collectionId?: string | null;
  /**
   * Static page template (`page.isStaticTemplate`): never publishes as its
   * own page and the Designer link picker excludes it.
   */
  isStaticTemplate?: boolean;
  /** Utility page key (`page.utilKey`): `404`, `search`, `usys-*`, ... */
  utilKey?: string | null;
  /** Unpublished draft row (`page.draft`). */
  draft?: boolean;
  /** Standalone site-branch page (`page.standaloneBranch`). */
  standaloneBranch?: boolean;
};

/**
 * Whether a page is a valid static page-link target, from its metadata alone.
 *
 * Excludes (fails closed when a link names one):
 * - dynamic pages (`collectionId`) — reached through a collection item,
 * - static page templates (`isStaticTemplate`) — never publish as their own
 *   page and the Designer link picker excludes them,
 * - utility pages (`utilKey`) except the allowed static Usys set
 *   ({@link allowedStaticUsysPages}) — `password`/`search`/`404`/commerce
 *   utility pages are not link targets,
 * - draft standalone-branch parents (`draft` + `standaloneBranch`) — the
 *   hidden fork parent; ordinary drafts stay linkable.
 *
 * Branch handling is intentionally NOT part of this predicate: a site-branch
 * working row is structurally linkable, but whether branch rows or their
 * main identities belong in a catalog depends on which tree the host is
 * editing (the server catalogs are main-only, WFS keeps the host's working
 * tree), so callers apply that themselves.
 */
export function isStaticPageLinkTarget(page: PageLinkTargetMetadata): boolean {
  if (page.collectionId) return false;
  if (page.isStaticTemplate) return false;
  if (page.utilKey && !allowedStaticUsysPages.has(page.utilKey)) return false;
  if (page.draft && page.standaloneBranch) return false;
  return true;
}
